export interface ActionableGoogleMimeBehavior {
  action: 'direct' | 'export';
  outMime: string;
  extension: string;
}

export type GoogleMimeTypeBehavior = {action: 'not_allowed'} | ActionableGoogleMimeBehavior;

export enum GoogleDriveIssueType {
  GoogleDriveUnreachable,
  UnableToExportFile,
  UnableToDirectDownloadFile,
  InvalidFilePicked,
}

export interface GoogleDriveIssue {
  issueType: GoogleDriveIssueType;
  userDescription: string;
  // The freeform, human-readable Google error message. Google embeds identifiers
  // (file id, email) here on some 4xx (e.g. 404 "File not found: {fileId}", 403
  // "...permissions for file {fileId}"), so treat it as potentially sensitive: it is
  // shown to the user who owns the file, but must NOT be logged except on the generic
  // 400 message (see the log gate in ValidateAndDownloadGoogleDriveFiles).
  googleErrorMessage?: string;
  // Google's structured, machine-readable error reason enum (e.g.
  // "insufficientFilePermissions", "rateLimitExceeded", "authError", "notFound").
  // These are fixed tokens that never embed a file id / email / filename, so unlike
  // googleErrorMessage this is safe to log on any status — it gives on-call the 4xx
  // sub-case a bare status code can't (permission vs rate-limit vs not-exportable).
  // null when the body is absent/non-JSON or carries no reason.
  googleErrorReason?: string | null;
}

export interface GoogleErrorDetails {
  message: string;
  reason: string | null;
}

export async function getGoogleErrorDetailsFromResponse(response: Response): Promise<GoogleErrorDetails> {
  // A non-OK response can carry a non-JSON body (e.g. an HTML 5xx page from a
  // proxy/gateway), which makes response.json() reject. Swallow that rejection and
  // fall back to the response's status text below so callers still get a graceful
  // GoogleDriveIssue instead of an unhandled promise rejection propagating out.
  const responseJson = await Promise.resolve(response.json()).catch(() => null);
  if (responseJson == null) {
    return {message: `${JSON.stringify(response.statusText || 'Unknown error')}`, reason: null};
  }
  const message = `${JSON.stringify(responseJson?.error?.message ?? 'Unknown error')}`;
  // `error.errors[0].reason` is Drive's legacy JSON-API reason enum; `error.status`
  // is the newer google.rpc enum (e.g. PERMISSION_DENIED). Prefer the former (always
  // present on Drive 4xx JSON errors), fall back to the latter. Both are safe to log.
  const reason: string | null =
    responseJson?.error?.errors?.[0]?.reason ?? responseJson?.error?.status ?? null;
  return {message, reason};
}

function getPrefixUserDescriptionForDownloadOrExport(response: Response): string {
  if (response.status >= 500 && response.status < 600) {
    return 'Google had an error processing your request to ';
  } else if (response.status > 400 && response.status < 500) {
    return 'Google rejected your request to ';
  } else {
    return 'There was an issue processing your request to ';
  }
}

export async function createUnableToDirectDownloadFileIssue(
  inputFileInfo: InputFileInfo,
  response: Response
): Promise<GoogleDriveIssue> {
  let suffixUserDescription: string = `download file "${inputFileInfo.inputFileName}"`;
  const {message: googleErrorMessage, reason: googleErrorReason} =
    await getGoogleErrorDetailsFromResponse(response);
  if (response.status > 400 && response.status < 500) {
    suffixUserDescription += `: ${googleErrorMessage}`;
  }

  return {
    issueType: GoogleDriveIssueType.UnableToDirectDownloadFile,
    userDescription: getPrefixUserDescriptionForDownloadOrExport(response) + suffixUserDescription,
    googleErrorMessage,
    googleErrorReason,
  };
}

export async function createUnableToExportFileIssue(
  inputFileInfo: InputFileInfo,
  outputFileInfo: ExportOutputFileInfo,
  response: Response
): Promise<GoogleDriveIssue> {
  let suffixUserDescription: string = `export file "${inputFileInfo.inputFileName}" to file type ${outputFileInfo.outputFileExtension}`;
  const {message: googleErrorMessage, reason: googleErrorReason} =
    await getGoogleErrorDetailsFromResponse(response);
  if (response.status > 400 && response.status < 500) {
    suffixUserDescription += `: ${googleErrorMessage}`;
  }

  return {
    issueType: GoogleDriveIssueType.UnableToExportFile,
    userDescription: getPrefixUserDescriptionForDownloadOrExport(response) + suffixUserDescription,
    googleErrorMessage,
    googleErrorReason,
  };
}

export const GoogleDriveUnreachableIssue: GoogleDriveIssue = {
  issueType: GoogleDriveIssueType.GoogleDriveUnreachable,
  userDescription: 'Cannot reach Google Drive',
};

export interface InputFileInfo {
  inputFileId: string;
  inputFileName: string;
  inputFileMimeType: string;
}

export interface DirectDownloadOutputFileInfo {
  outputFileName: string;
  outputFileMimeType: string;
}

export interface ExportOutputFileInfo extends DirectDownloadOutputFileInfo {
  outputFileExtension: string;
}

export type DownloadSingleGoogleDriveFileInstructions =
  | {
      action: 'direct';
      inputFileInfo: InputFileInfo;
      outputFileInfo: DirectDownloadOutputFileInfo;
    }
  | {
      action: 'export';
      inputFileInfo: InputFileInfo;
      outputFileInfo: ExportOutputFileInfo;
    };

export type DownloadResult =
  | {fileId: string; file: File; error: null}
  | {fileId: string; error: GoogleDriveIssue; file: null};

export function createInvalidFilePickedDownloadResult(
  fileId: string,
  userDescription: string
): DownloadResult {
  return {
    fileId,
    file: null,
    error: {
      issueType: GoogleDriveIssueType.InvalidFilePicked,
      userDescription,
    },
  };
}
